import React, { useState, useEffect } from 'react';

import { Link as RouterLink } from 'react-router-dom';

import {
  makeStyles,
  Typography,
  Card,
  Box,
  Container,
  Link,
  Button,
  Dialog,
  DialogTitle,
  TextField,
  List,
  ListItem,
  ListItemText,
  FormControlLabel,
  Checkbox,
  SvgIcon,
  FormControl
} from '@material-ui/core';

import Autocomplete from '@material-ui/lab/Autocomplete';

import { Download as DownloadIcon } from 'react-feather';

import 'moment/locale/es';
import moment from 'moment';

import { t } from 'src/shared/literals';
import Page from 'src/shared/ui/components/Page';
import Form from 'src/shared/ui/components/Form';
import processFormErrorMessages from 'src/shared/ui/components/Form/FormErrorMessage';

import validateSchema from 'src/shared/models/SchemaValidator';
import Contract from 'src/contract/models/Contract';

import createContract from 'src/contract/use-cases/createContract';
import getContractById from 'src/contract/use-cases/getContractById';
import updateContractById from 'src/contract/use-cases/updateContractById';

import renewContractById from 'src/contract/use-cases/renewContractById';
import getContractTypeList from 'src/contract-type/use-cases/getContractTypeList';
import getContractStatusList from 'src/contract-status/use-cases/getContractStatusList';
import getLocatorTypeList from 'src/locator-type/use-cases/getLocatorTypeList';
import getInventoryList from 'src/inventory/use-cases/getInventoryList';
import getInventoryById from 'src/inventory/use-cases/getInventoryById';
import getCustomerContactList from 'src/customer-contact/use-cases/getCustomerContactList';

import getEmployeeList from 'src/employee/use-cases/getEmployeeList';

import getContractList from 'src/contract/use-cases/getContractList';
import downloadContractDocumentPdfByIndex from 'src/contract/use-cases/downloadContractDocumentPdfByIndex';

import getCustomerList from 'src/customer/use-cases/getCustomerList';
import sendDeviceInventoryCommandByInventoryId from 'src/inventory/use-cases/sendDeviceInventoryCommandByInventoryId';

import uploadFile from 'src/shared/use-cases/uploadFile';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.dark,
    minHeight: '100%',
    paddingBottom: theme.spacing(3),
    paddingTop: theme.spacing(3)
  },
  saveButton: {
    top: '-20px'
  },
  backButton: {
    top: '-20px',
    marginRight: theme.spacing(1)
  },
  documentButton: {
    top: '-20px',
    marginRight: theme.spacing(1)
  },
  documentButtonRed: {
    background: 'red',
    color: 'white',
    top: '-20px',
    marginRight: theme.spacing(1)
  },
  testInstallationButton: {
    top: '-20px',
    marginRight: theme.spacing(1)
  },
  renewalButton: {
    top: '-20px',
    marginRight: theme.spacing(1)
  },
  registerInstallationButton: {
    top: '16px',
    position: 'absolute',
    right: '10px',
    float: 'right'
  },
  getSimcardButton: {
    top: '80px',
    position: 'absolute',
    right: '20px',
    marginRight: theme.spacing(1)
  },
  stopEngineButton: {
    top: '110px',
    position: 'absolute',
    right: '20px',
    marginRight: theme.spacing(1)
  },
  startEngineButton: {
    position: 'absolute',
    right: '20px',
    top: '140px',
    marginRight: theme.spacing(1)
  },
  initConfigButton: {
    top: '170px',
    position: 'absolute',
    right: '20px',
    marginRight: theme.spacing(1)
  },
  searchButton: {
    top: '-20px',
    marginRight: theme.spacing(1)
  },
  scrollBox: {
    overflow: 'scroll',
    height: '80vh'
  },
  dialogContainer: {
    width: '600px',
    minHeight: '100px'
  },
  documentsDialogContainer: {
    width: '600px',
    minHeight: '100px'
  },
  contractRenewalDialog: {
    width: '600px',
    minHeight: '100px'
  },
  contractRenewalDialogContainer: {
    minHeight: '100px'
  },
  dialogListItemSecondary: {
    textAlign: 'center',
    color: 'gray',
    backgroundColor: '#fff',
    padding: '6px 16px',
    marginLeft: '10%',
    width: '80%',
    borderRadius: 5
  },
  dialogListItemPrimary: {
    textAlign: 'center',
    color: '#fff',
    backgroundColor: '#4caf50',
    padding: '6px 16px',
    width: '80%',
    marginLeft: '10%',
    marginTop: '20px',
    borderRadius: 5
  },
  formControl: {
    width: '200px',
    margin: theme.spacing(1)
  },
  formControlHalfWidth: {
    width: '350px'
  },
  formControlHalfWidthSimcard: {
    paddingTop: '20px',
    width: '350px'
  },
  selectField: {
    margin: 2,
    paddingTop: '17px'
  },
  checkboxFieldDocument: {
    margin: 1,
    paddingTop: '2px'
  },
  documentPending: {
    background: 'red',
    color: 'white'
  },
  documentUploaded: {
    background: 'green',
    color: 'white'
  },
  optionalDocumentUploaded: {
    background: 'orange',
    color: 'white'
  },
  documentTable: {
    textAlign: 'center',
    margin: '5px',
    border: '1px solid'
  },
  optionalDocumentTable: {
    textAlign: 'center',
    margin: '5px',
    border: '2px solid orange'
  },
  downloadButton: {
    color: 'blue'
  },
  deviceInputStatusOn: {
    color: 'white',
    background: 'green'
  },
  deviceInputStatusOff: {
    color: 'white',
    background: 'red'
  }
}));

const DOCUMENT_TYPES = [
  'contract_activation',
  'informconf',
  'identity_card',
  'vehicle_identity_card',
  'work_sheet',
  'directives',
  'sworn_declaration'
];

const INACTIVE_WITH_NOTICE_DOCUMENT_TYPES = ['inactive_with_notice_sheet'];
const INACTIVE_CONTRACT_DOCUMENT_TYPES = ['uninstallation_note'];

const OPTIONAL_DOCUMENT_TYPES = [
  'promissory_note',
  'billing_order',
  'debit_sheet',
  'assessment',
  'annex'
];

const ContractForm = ({ contractId, customerId, cancel, success }) => {
  const classes = useStyles();
  const user = JSON.parse(localStorage.user);

  const [companyHeadquarterId, setCompanyHeadquarterId] = useState(
    localStorage.selectedUserHeadquarter
  );

  const [formFields, setFormFields] = useState([]);
  const [contractStatusActiveId, setContractStatusActiveId] = useState(null);
  const [
    contractStatusActiveReinstallation,
    setContractStatusActiveReinstallation
  ] = useState(null);
  const [
    contractStatusActiveHolderChange,
    setContractStatusActiveHolderChange
  ] = useState(null);
  const [
    contractStatusActivePendingPayment,
    setContractStatusActivePendingPayment
  ] = useState(null);

  const [isRequesting, setIsRequesting] = useState(false);
  const [contractData, setContractData] = useState(null);
  const [
    contractMandatoryDocumentTypes,
    setContractMandatoryDocumentTypes
  ] = useState([]);
  const [contractIdState, setContractIdState] = useState(contractId);
  const [showLastPositionsDialog, setShowLastPositionsDialog] = useState(false);
  const [lastPositions, setLastPositions] = useState([]);
  const [deviceInputsStatus, setDeviceInputsStatus] = useState({});
  const [selectedDeviceField, setSelectedDeviceField] = useState('');
  const [selectedDeviceInventory, setSelectedDeviceInventory] = useState({
    id: null
  });

  const [selectedSimcardField, setSelectedSimcardField] = useState('');
  const [selectedSimcardInventory, setSelectedSimcardInventory] = useState({
    id: null
  });

  const [
    showContractDocumentsDialog,
    setShowContractDocumentsDialog
  ] = useState(false);

  const [
    showContractDocumentUploadDialog,
    setShowContractDocumentUploadDialog
  ] = useState(false);

  const [showContractRenewalDialog, setShowContractRenewalDialog] = useState(
    false
  );

  const [contractTypeList, setContractTypeList] = useState([]);

  const [documentUploadType, setDocumentUploadType] = useState({});

  const [
    selectDeviceAutoCompleteOptions,
    setSelectDeviceAutoCompleteOptions
  ] = useState([]);

  const [
    selectSimcardAutoCompleteOptions,
    setSelectSimcardAutoCompleteOptions
  ] = useState([]);

  const [isChangingLocatorDevice, setIsChangingLocatorDevice] = useState(false);
  const [isChangingSimcard, setIsChangingSimcard] = useState(false);

  const handleUploadDocument = async () => {
    try {
      let file = document.getElementById('file');
      if (!file.files || !file.files[0]) {
        alert('Debe seleccionar un archivo pdf o jpg');
        return;
      }

      file = file.files[0];

      if (
        file.type.indexOf('pdf') === -1 &&
        file.type.indexOf('jpeg') === -1 &&
        file.type.indexOf('jpg') === -1
      ) {
        alert('El formato del archivo debe ser pdf o jpg');
        return;
      }

      if (!Object.keys(documentUploadType).length) {
        alert('Debe seleccionar al menos un tipo de documento');
        return;
      }

      await uploadFile(
        `customer-contracts/upload/document?customerContractId=${
          contractData.id
        }&documentType=${Object.keys(documentUploadType).toString()}`,
        file
      );

      let documents = contractData.documents || [];
      const user = JSON.parse(localStorage.user);
      documents.push({
        userFullName: `${user.firstname} ${user.surnames}`,
        timestamp: new Date().getTime(),
        type: Object.keys(documentUploadType)
      });

      let updatedContractData = { ...contractData };
      updatedContractData.documents = documents;
      alert('Documento subido');
      setContractData(updatedContractData);

      setShowContractDocumentUploadDialog(false);
    } catch (e) {
      alert('Ha ocurrido un error subiendo el archivo:' + e);
      setShowContractDocumentUploadDialog(true);
    }
  };

  const initFormFields = async (fields) => {
    const contractTypeList = getContractTypeList({
      companyHeadquarterId,
      page: null,
      limit: null,
      orderBy: ''
    });

    const contractStatusList = getContractStatusList({
      page: null,
      limit: null,
      orderBy: ''
    });
    const locatorTypeList = getLocatorTypeList({
      page: null,
      limit: null,
      orderBy: ''
    });
    const customerContactList = getCustomerContactList({
      headquarterCustomerId: customerId,
      page: null,
      limit: null,
      orderBy: ''
    });
    const employeeList = getEmployeeList({
      companyHeadquarterId,
      page: null,
      limit: null,
      orderBy: ''
    });

    const customerContractList = getContractList({
      customerId,
      page: 0,
      limit: 10000,
      search: '',
      orderBy: ''
    });

    Promise.all([
      contractTypeList,
      contractStatusList,
      locatorTypeList,
      customerContactList,
      employeeList,
      customerContractList
    ]).then((responseList) => {
      if (fields) {
        setContractData(fields);

        if (fields.id) {
          let contractDocumentTypes = DOCUMENT_TYPES;
          if (
            fields.ContractStatus &&
            fields.ContractStatus.code === 'INACTIVE_WITH_NOTICE'
          ) {
            contractDocumentTypes = contractDocumentTypes.concat(
              INACTIVE_WITH_NOTICE_DOCUMENT_TYPES
            );

            contractDocumentTypes = contractDocumentTypes.concat(
              INACTIVE_CONTRACT_DOCUMENT_TYPES
            );
          }

          if (
            fields.ContractStatus &&
            ['INACTIVE_PENDING_UNINSTALLATION', 'INACTIVE'].includes(
              fields.ContractStatus.code
            )
          ) {
            contractDocumentTypes = contractDocumentTypes.concat(
              INACTIVE_WITH_NOTICE_DOCUMENT_TYPES
            );
          }

          setContractMandatoryDocumentTypes(contractDocumentTypes);
        }
      }

      const user = JSON.parse(localStorage.user);

      setContractTypeList(responseList[0].rows);

      const contract = Contract({
        fields,
        user,
        contractTypeList: responseList[0].rows,
        contractStatusList: responseList[1],
        locatorTypeList: responseList[2],
        customerContactList: responseList[3].rows,
        employeeList: responseList[4].rows,
        customerContractList: responseList[5].rows
      });

      setFormFields([]);
      setFormFields(contract);

      const contractStatusActive = responseList[1].filter(
        (x) => x.code === 'ACTIVE'
      )[0];
      const contractStatusActiveReinstallation = responseList[1].filter(
        (x) => x.code === 'ACTIVE_REINSTALLATION'
      )[0];
      const contractStatusActiveHolderChange = responseList[1].filter(
        (x) => x.code === 'ACTIVE_HOLDER_CHANGE'
      )[0];
      const contractStatusActivePendingPayment = responseList[1].filter(
        (x) => x.code === 'ACTIVE_PENDING_PAYMENT'
      )[0];
      setContractStatusActiveId(contractStatusActive.id);
      setContractStatusActiveReinstallation(contractStatusActiveReinstallation);
      setContractStatusActiveHolderChange(contractStatusActiveHolderChange);
      setContractStatusActivePendingPayment(contractStatusActivePendingPayment);

      console.log(contract);
    });
  };

  const fetchForm = async () => {
    const contract = await getContractById(contractIdState);

    initFormFields(contract);
    setCompanyHeadquarterId(
      localStorage.selectedUserHeadquarter === 'all'
        ? contract.companyHeadquarterId
        : localStorage.selectedUserHeadquarter
    );
  };

  useEffect(() => {
    if (contractIdState === 'new') {
      initFormFields();
    } else {
      fetchForm();
    }

    return () => {
      clearInterval(window.lastPositionsInterval);
    };
  }, []);

  const validateFormFields = async (formFieldsValues) => {
    const validatedFormFields = await validateSchema(formFieldsValues);
    formFieldsValues = processFormErrorMessages(
      validatedFormFields,
      formFieldsValues
    );

    setFormFields(formFieldsValues.fields);

    return {
      errors: formFieldsValues.errors,
      fields: validatedFormFields
    };
  };

  const handleChangeFormFieldsValues = (formFieldsValues, target) => {
    if (target.name === 'contractTypeId') {
      let fields = {
        [target.name]: target.value
      };

      initFormFields(fields);

      return;
    }

    if (
      target.name === 'contractStatusId' &&
      target.value === contractStatusActiveId
    ) {
      const isConfirmed = confirm(
        '¿Está seguro? La fecha de inicio de contrato será la de la fecha de inicio establecida y se procederá a generar las cuotas'
      );
      if (!isConfirmed) {
        return;
      }
    }

    setFormFields(formFieldsValues);
  };

  const searchCustomer = async () => {
    const customerSearchText = formFields && formFields[0].value;

    if (!customerSearchText || customerSearchText.length < 3) {
      alert('Introduzca al menos 3 caracteres');
      return;
    }

    const companyHeadquarterId = localStorage.selectedUserHeadquarter;
    const customerList = await getCustomerList({
      companyHeadquarterId,
      search: customerSearchText,
      orderBy: '',
      page: 0,
      limit: 10
    });

    if (customerList && customerList.count) {
      const customer = customerList.rows[0];

      const customerFullName = `CL${customer.customerCode}. ${customer.firstname} ${customer.surnames}. DOC:${customer.document}`;

      customerId = customer.id;
      contractData.customer = customerFullName;
      contractData.newHeadquarterCustomerId = customer.id;
      contractData.ContractStatus = contractStatusActiveHolderChange;
      contractData.contractStatusId = contractStatusActiveHolderChange.id;
      contractData.holderChangeDestinContractId = contractData.id;
      contractData.beginDate =
        contractData.renewalDate || contractData.beginDate;

      initFormFields(contractData);
    } else {
      alert('No se encontró ningún cliente con los datos introducidos');
    }
  };

  const postForm = async () => {
    if (isRequesting) {
      return;
    }

    const validatedForm = await validateFormFields(formFields);
    console.log(validatedForm);
    if (validatedForm.errors) {
      alert('Rellene todos los campos correctamente.');
      return;
    }

    setIsRequesting(true);

    let result;
    if (contractIdState === 'new') {
      validatedForm.fields.headquarterCustomerId = customerId;
      validatedForm.fields.companyHeadquarterId = companyHeadquarterId;

      result = await createContract(validatedForm.fields);
    } else {
      result = await updateContractById(validatedForm.fields, contractIdState);
    }

    setIsRequesting(false);

    if (result && result.error) {
      alert(t(result.error) || 'Se ha producido un error, vuelva a intentarlo');
      return;
    } else if (!result.id) {
      alert('Se ha producido un error, vuelva a intentarlo');
      return;
    }

    success();
  };

  const reinstallContract = () => {
    alert('A continuación rellene los datos del nuevo contrato a reinstalar');

    let fields = JSON.parse(JSON.stringify(contractData));

    fields.reinstallationDestinContractId = contractData.id;
    fields.holderChangeDestinContractId = null;
    fields.id = null;
    fields.ContractStatus = contractStatusActiveReinstallation;
    fields.contractStatusId = contractStatusActiveReinstallation.id;
    fields.contractCode = null;
    fields.color = null;
    fields.identification = null;
    fields.beginDate = contractData.renewalDate || contractData.beginDate;
    fields.endDate = null;
    fields.renewalDate = null;
    fields.plannedInstallationDate = new Date();
    fields.brand = null;
    fields.model = null;
    fields.year = null;
    fields.itemName = null;

    setContractIdState('new');
    initFormFields(fields);
  };

  const changeHolderContract = () => {
    alert('A continuación rellene el nuevo cliente');

    let fields = JSON.parse(JSON.stringify(contractData));

    fields.headquarterCustomerId = null;
    fields.ContractStatus = contractStatusActiveHolderChange;
    fields.contractStatusId = contractStatusActiveHolderChange.id;
    fields.contractCode = null;
    fields.beginDate = contractData.renewalDate || contractData.beginDate;
    fields.endDate = null;
    fields.renewalDate = null;
    fields.plannedInstallationDate = new Date();
    fields.holderCustomerContactId = null;
    fields.beneficiaryCustomerContactId = null;
    fields.billToCustomerContactId = null;
    fields.moneyCollectionCustomerContactId = null;
    fields.directives1CustomerContactId = null;
    fields.directives2CustomerContactId = null;
    fields.directives3CustomerContactId = null;
    fields.directives4CustomerContactId = null;
    fields.directives5CustomerContactId = null;
    fields.info1CustomerContactId = null;
    fields.info2CustomerContactId = null;
    fields.info3CustomerContactId = null;
    fields.info4CustomerContactId = null;
    fields.info5CustomerContactId = null;

    setContractIdState('new');
    initFormFields(fields);
  };

  const getContractCode = () => {
    if (contractIdState !== 'new' && contractData) {
      return `${contractData.contractCodePrefix}${contractData.contractCode}`;
    }

    return 'Nuevo';
  };

  const isInactiveReinstallation = () => {
    return (
      contractIdState !== 'new' &&
      contractData &&
      contractData.ContractStatus &&
      contractData.ContractStatus.code === 'INACTIVE_REINSTALLATION'
    );
  };

  const isInactiveHolderChange = () => {
    return (
      contractIdState !== 'new' &&
      contractData &&
      contractData.ContractStatus &&
      contractData.ContractStatus.code === 'INACTIVE_HOLDER_CHANGE'
    );
  };

  const handleLastPositionsDialogClose = () => {
    setShowLastPositionsDialog(false);
    clearInterval(window.lastPositionsInterval);
  };

  const showTestInstallation = () => {
    setShowLastPositionsDialog(true);
    setLastPositions([]);
    setSelectedDeviceInventory({
      id: null
    });
    setSelectedSimcardInventory({
      id: null
    });
    clearInterval(window.lastPositionsInterval);

    checkAvailableDevices();
    checkAvailableSimcards();
  };

  const checkAvailableDevices = async () => {
    const laboratoryDevices = await getInventoryList({
      companyHeadquarterId,
      filterCriteria: 'location:installer',
      search: '',
      orderBy: 'code ASC',
      page: 0,
      limit: ''
    });

    const options = laboratoryDevices.rows.map((device) => {
      //if (device.ProductType.code.indexOf('SIMCARD') === -1) {
      const key = `${device.code} (${device.identification})`;

      return {
        name: key,
        id: device.id.toLowerCase()
      };
      //}
    });

    if (
      contractData.inventoryList &&
      contractData.inventoryList.locatorDevice
    ) {
      const inventoryDevice = await getInventoryById(
        contractData.inventoryList.locatorDevice.inventoryId
      );
      const { id, identification, code } = inventoryDevice;
      const device = {
        id,
        identification,
        code
      };

      const selectedOption = {
        name: `${device.code} (${device.identification})`,
        id: device.id.toLowerCase()
      };

      const mergedOptions = [selectedOption].concat(options);

      setSelectedDeviceField(selectedOption.name);
      setSelectDeviceAutoCompleteOptions(mergedOptions);

      handleSelectLastPositionsImei({
        target: {
          id: selectedOption.id
        }
      });
    } else {
      setSelectDeviceAutoCompleteOptions(options);
    }
  };

  const checkAvailableSimcards = async () => {
    const laboratory = await getInventoryList({
      companyHeadquarterId,
      filterCriteria: 'location:installer',
      search: '',
      orderBy: 'code ASC',
      page: 0,
      limit: ''
    });

    const options = laboratory.rows.map((device) => {
      //if (device.ProductType.code.indexOf('SIMCARD') !== -1) {
      const key = device.identification;

      return {
        name: key,
        id: device.id.toLowerCase()
      };
      //}
    });

    if (contractData.inventoryList && contractData.inventoryList.simcard) {
      const inventory = await getInventoryById(
        contractData.inventoryList.simcard.inventoryId
      );
      const { id, identification } = inventory;
      const simcard = {
        id,
        identification
      };

      const selectedOption = {
        name: identification,
        id: id.toLowerCase()
      };

      const mergedOptions = [selectedOption].concat(options);

      setSelectedSimcardField(selectedOption.name);
      setSelectSimcardAutoCompleteOptions(mergedOptions);

      setSelectedSimcardInventory({
        id: simcard.id.toLowerCase()
      });
    } else {
      setSelectSimcardAutoCompleteOptions(options);
    }
  };

  const checkLastPositions = async (inventoryId) => {
    const inventoryDevice = await getInventoryById(inventoryId);

    const lastPositions = inventoryDevice?.lastPositions?.lastHourPositionList
      .length
      ? inventoryDevice.lastPositions.lastHourPositionList
      : [];

    const lastPosition = inventoryDevice?.lastPositions
      ? inventoryDevice.lastPositions.lastPosition
      : false;

    setLastPositions(lastPositions);

    if (lastPosition) {
      const lastDeviceInputStatus = {
        ignitionOn: typeof lastPosition['sensor.1.event'] !== 'undefined',
        ignitionOff: typeof lastPosition['sensor.2.event'] !== 'undefined',
        batteryConnected: typeof lastPosition['sensor.5.event'] !== 'undefined',
        batteryDisconnected:
          typeof lastPosition['sensor.4.event'] !== 'undefined'
      };
      setDeviceInputsStatus(lastDeviceInputStatus);
    }
  };

  const handleSelectLastPositionsImei = async (e) => {
    const { id } = e.target;

    if (id) {
      clearInterval(window.lastPositionsInterval);
      const inventoryDevice = await getInventoryById(id);

      setSelectedDeviceInventory({
        id,
        productTypeCode: inventoryDevice.ProductType.code
      });

      checkLastPositions(id);

      window.lastPositionsInterval = setInterval(() => {
        checkLastPositions(id);
      }, 5000);
    }
  };

  const canRegisterInstallation = () => {
    const isMasterUser = ['Prieto González', 'Coronel Cantero'].includes(
      user.surnames
    );

    const hasSomePositions = lastPositions?.length > 0;

    const hasDeviceAllInputsStatus =
      (deviceInputsStatus.ignitionOn &&
        deviceInputsStatus.ignitionOff &&
        (selectedDeviceInventory?.productTypeCode?.indexOf('JIMI') !== -1
          ? true
          : deviceInputsStatus.batteryConnected) &&
        deviceInputsStatus.batteryDisconnected) ||
      isMasterUser;

    console.log({
      action: 'canRegisterInstallation',
      isMasterUser,
      hasDeviceAllInputsStatus,
      deviceInputsStatus,
      isChangingLocatorDevice,
      selectedDeviceInventory,
      selectedSimcardInventory,
      hasSomePositions,
      lastPositions,
      contractData
    });

    return (
      hasSomePositions &&
      selectedDeviceInventory.id &&
      contractData &&
      contractData.ContractStatus &&
      ['PENDING_INSTALLATION'].includes(contractData.ContractStatus.code) &&
      hasDeviceAllInputsStatus
    );
  };

  const canRegisterDeviceChange = () => {
    const isMasterUser = ['Prieto González', 'Coronel Cantero'].includes(
      user.surnames
    );
    const hasSomePositions = lastPositions?.length > 0;

    const hasDeviceAllInputsStatus =
      (deviceInputsStatus.ignitionOn &&
        deviceInputsStatus.ignitionOff &&
        (selectedDeviceInventory?.productTypeCode?.indexOf('JIMI') !== -1
          ? true
          : deviceInputsStatus.batteryConnected) &&
        deviceInputsStatus.batteryDisconnected) ||
      isMasterUser;

    console.log({
      action: 'canRegisterDeviceChange',
      isMasterUser,
      hasDeviceAllInputsStatus,
      deviceInputsStatus,
      isChangingLocatorDevice,
      selectedDeviceInventory,
      selectedSimcardInventory,
      hasSomePositions,
      lastPositions,
      contractData
    });

    return (
      isChangingLocatorDevice &&
      selectedDeviceInventory.id &&
      lastPositions.length &&
      contractData &&
      contractData.ContractStatus &&
      ['ACTIVE'].includes(contractData.ContractStatus.code) &&
      hasDeviceAllInputsStatus
    );
  };

  const registerInstallation = () => {
    handleLastPositionsDialogClose();

    let fields = JSON.parse(JSON.stringify(contractData));
    fields.ContractStatus = contractStatusActivePendingPayment;
    fields.contractStatusId = contractStatusActivePendingPayment.id;
    fields.beginDate = new Date();
    fields.installation;
    fields.inventoryDeviceId = selectedDeviceInventory.id;
    fields.inventorySimcardId = selectedSimcardInventory.id;

    initFormFields(fields);
  };

  const registerDeviceChange = () => {
    handleLastPositionsDialogClose();

    let fields = JSON.parse(JSON.stringify(contractData));
    fields.oldInventoryDeviceId =
      contractData.inventoryList.locatorDevice.inventoryId;
    fields.inventoryDeviceId = selectedDeviceInventory.id;

    if (contractData.inventoryList.simcard) {
      fields.oldInventorySimcardId =
        contractData.inventoryList.simcard.inventoryId;
    }

    if (selectedSimcardInventory) {
      fields.inventorySimcardId = selectedSimcardInventory.id;
    }

    initFormFields(fields);
  };

  const sendDeviceCommand = async (command, channel, simcardInventoryId) => {
    try {
      const response = await sendDeviceInventoryCommandByInventoryId({
        inventoryId: selectedDeviceInventory.id,
        simcardInventoryId,
        command,
        channel,
        contractCode: contractData.contractCode
      });

      if (response.error) {
        alert('Se ha producido un error');
        return;
      }

      alert('Comando enviado');
    } catch (e) {
      console.log(e);
      alert('Se ha producido un error');
    }
  };

  const renderInstallationDialog = () => {
    return (
      <Dialog
        onClose={handleLastPositionsDialogClose}
        aria-labelledby="simple-dialog-title"
        open={showLastPositionsDialog}
      >
        <div className={classes.dialogContainer}>
          <DialogTitle id="simple-dialog-title">
            <FormControl
              className={classes.formControlHalfWidth}
              key="selectedDeviceImei"
            >
              <Autocomplete
                id={`select-field-label-select-device`}
                options={selectDeviceAutoCompleteOptions}
                getOptionLabel={(option) => option.name}
                name="selectedDeviceImei"
                inputValue={selectedDeviceField}
                autoComplete="off"
                onInputChange={(event, newInputValue) => {
                  setSelectedDeviceField(newInputValue);
                }}
                onChange={(event, newInputValue) => {
                  if (newInputValue) {
                    if (
                      contractData?.inventoryList?.locatorDevice?.inventoryId &&
                      newInputValue.id !==
                        contractData.inventoryList.locatorDevice.inventoryId
                    ) {
                      setIsChangingLocatorDevice(true);
                    } else {
                      setIsChangingLocatorDevice(false);
                    }

                    setSelectedDeviceField(newInputValue.name);
                    handleSelectLastPositionsImei({
                      target: {
                        id: newInputValue.id
                      }
                    });
                  }
                }}
                noOptionsText={'Sin resultados'}
                renderInput={(params) => (
                  <TextField
                    autoComplete="off"
                    InputProps={{
                      autoComplete: 'off'
                    }}
                    {...params}
                    label="Seleccione el dispositivo con ubicación instalador"
                    variant="outlined"
                  />
                )}
              />
            </FormControl>

            {selectedDeviceInventory &&
            selectedDeviceInventory.productTypeCode &&
            selectedDeviceInventory.productTypeCode.indexOf('JIMI') !== -1 ? (
              <FormControl
                className={classes.formControlHalfWidthSimcard}
                key="selectedSimcardIccid"
              >
                <Autocomplete
                  id={`select-field-label-select-simcard`}
                  options={selectSimcardAutoCompleteOptions}
                  getOptionLabel={(option) => option.name}
                  name="selectedSimcardIccid"
                  inputValue={selectedSimcardField}
                  autoComplete="off"
                  onInputChange={(event, newInputValue) => {
                    setSelectedSimcardField(newInputValue);
                  }}
                  onChange={(event, newInputValue) => {
                    if (newInputValue) {
                      if (
                        contractData?.inventoryList?.simcard?.inventoryId &&
                        newInputValue.id !==
                          contractData.inventoryList.simcard.inventoryId
                      ) {
                        setIsChangingSimcard(true);
                      } else {
                        setIsChangingSimcard(false);
                      }

                      setSelectedSimcardField(newInputValue.name);
                      setSelectedSimcardInventory({
                        id: newInputValue.id,
                        productTypeCode: newInputValue.productTypeCode
                      });
                    }
                  }}
                  noOptionsText={'Sin resultados'}
                  renderInput={(params) => (
                    <TextField
                      autoComplete="off"
                      InputProps={{
                        autoComplete: 'off'
                      }}
                      {...params}
                      label="Seleccione línea con ubicación instalador"
                      variant="outlined"
                    />
                  )}
                />
              </FormControl>
            ) : (
              ''
            )}

            {canRegisterInstallation() ? (
              <Button
                color="primary"
                variant="contained"
                className={classes.registerInstallationButton}
                onClick={registerInstallation}
              >
                Registrar instalación
              </Button>
            ) : (
              ''
            )}

            {!canRegisterInstallation() && canRegisterDeviceChange() ? (
              <Button
                color="primary"
                variant="contained"
                className={classes.registerInstallationButton}
                onClick={registerDeviceChange}
              >
                Registrar cambio
              </Button>
            ) : (
              ''
            )}

            {selectedDeviceInventory.id &&
            selectedDeviceInventory.productTypeCode &&
            selectedDeviceInventory.productTypeCode.indexOf('JIMI') === -1 &&
            contractData &&
            contractData.ContractStatus &&
            (['PENDING_INSTALLATION'].includes(
              contractData.ContractStatus.code
            ) ||
              (['ACTIVE'].includes(contractData.ContractStatus.code) &&
                isChangingLocatorDevice)) ? (
              <Button
                color="primary"
                className={classes.getSimcardButton}
                onClick={() =>
                  sendDeviceCommand('GET_SIMCARD_ICCID_COMMAND', 'GPRS')
                }
              >
                Asociar línea (GPRS)
              </Button>
            ) : (
              ''
            )}

            {selectedDeviceInventory.id &&
            selectedDeviceInventory.productTypeCode &&
            selectedDeviceInventory.productTypeCode.indexOf('JIMI') !== -1 &&
            selectedSimcardInventory.id &&
            contractData &&
            contractData.ContractStatus &&
            (['PENDING_INSTALLATION'].includes(
              contractData.ContractStatus.code
            ) ||
              (['ACTIVE'].includes(contractData.ContractStatus.code) &&
                isChangingLocatorDevice)) ? (
              <Button
                color="primary"
                className={classes.initConfigButton}
                onClick={() =>
                  sendDeviceCommand(
                    'INIT_CONFIG',
                    'SMS',
                    selectedSimcardInventory.id
                  )
                }
              >
                Enviar configuración (SMS)
              </Button>
            ) : (
              ''
            )}

            {(selectedDeviceInventory.id || isChangingSimcard) &&
            selectedSimcardInventory?.id ? (
              <>
                <Button
                  color="primary"
                  className={classes.stopEngineButton}
                  onClick={() =>
                    sendDeviceCommand(
                      'SEND_STOP_ENGINE_COMMAND',
                      'SMS',
                      selectedSimcardInventory.id
                    )
                  }
                >
                  Parar motor (SMS)
                </Button>
                <Button
                  color="primary"
                  className={classes.startEngineButton}
                  onClick={() =>
                    sendDeviceCommand(
                      'SEND_START_ENGINE_COMMAND',
                      'SMS',
                      selectedSimcardInventory.id
                    )
                  }
                >
                  Arrancar motor (SMS)
                </Button>
              </>
            ) : (
              ''
            )}
          </DialogTitle>
        </div>
        {selectedDeviceInventory.id && (
          <div className={classes.dialogContainer}>
            <>
              <br></br>
              <br></br>
              <DialogTitle>Estado de entradas</DialogTitle>
              <table border="1" width="100%">
                <thead>
                  <th
                    className={
                      deviceInputsStatus.ignitionOn
                        ? classes.deviceInputStatusOn
                        : classes.deviceInputStatusOff
                    }
                  >
                    Contacto ON
                  </th>
                  <th
                    className={
                      deviceInputsStatus.ignitionOff
                        ? classes.deviceInputStatusOn
                        : classes.deviceInputStatusOff
                    }
                  >
                    Contacto OFF
                  </th>
                  <th
                    className={
                      deviceInputsStatus.batteryConnected
                        ? classes.deviceInputStatusOn
                        : classes.deviceInputStatusOff
                    }
                  >
                    Batería conectada
                  </th>
                  <th
                    className={
                      deviceInputsStatus.batteryDisconnected
                        ? classes.deviceInputStatusOn
                        : classes.deviceInputStatusOff
                    }
                  >
                    Batería desconectada
                  </th>
                </thead>
              </table>
            </>
            {lastPositions.length ? (
              <>
                <DialogTitle id="simple-dialog-title">
                  Últimas posiciones
                </DialogTitle>

                <table border="1" width="100%">
                  <thead>
                    <th>Fecha recepción</th>
                    <th>Fecha dispositivo</th>
                    <th>Evento</th>
                    <th>Entradas</th>
                    <th>Salidas</th>
                    <th>ºC</th>
                    <th>Voltaje</th>
                    <th>Coordenadas</th>
                  </thead>
                  <tbody>
                    {lastPositions.map((lastPosition, i) => {
                      return (
                        <tr key={i}>
                          <td>
                            {moment(lastPosition.reception_date)
                              .format('YY/MM/DD hh:mm:ss')
                              .toUpperCase()}
                          </td>
                          <td>
                            {moment(lastPosition.device_date)
                              .format('YY/MM/DD hh:mm:ss')
                              .toUpperCase()}
                          </td>
                          <td>
                            {lastPosition.event} ({lastPosition.eventLiteral})
                          </td>
                          <td>{lastPosition.inputs || ''}</td>
                          <td>{lastPosition.outputs || ''}</td>
                          <td>{lastPosition.temperature || ''}</td>
                          <td>{lastPosition.voltage || ''}</td>
                          <td>
                            {lastPosition.lat},{lastPosition.lng}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </>
            ) : (
              'El dispositivo no ha reportado en la última hora'
            )}
          </div>
        )}
      </Dialog>
    );
  };

  const handleCloseContractDocumentsDialog = () => {
    setShowContractDocumentsDialog(false);
  };

  const handleShowUploadDocument = () => {
    setDocumentUploadType({});
    setShowContractDocumentUploadDialog(true);
  };

  const handleCloseUploadDocumentDialog = () => {
    setShowContractDocumentsDialog(false);
    setShowContractDocumentUploadDialog(false);
    setDocumentUploadType({});
  };

  const hasContractDocumentType = (documentType) => {
    return (
      contractData &&
      contractData.documents &&
      contractData.documents.some((document) =>
        document.type.includes(documentType)
      )
    );
  };

  const hasContractSomePendingDocument = () => {
    if (!contractData || !contractData.ContractStatus) {
      return true;
    }

    const contractDocuments = contractData.documents;

    if (
      (['INACTIVE'].includes(contractData.ContractStatus.code) &&
        !contractDocuments) ||
      ['CANCELLED', 'REJECTED'].includes(contractData.ContractStatus.code)
    ) {
      return false;
    }

    if (!contractDocuments || !contractDocuments.length) {
      return true;
    }

    let documentsCount = [];
    for (const document of contractDocuments) {
      const mandatoryDocumentType = document.type
        .filter((x) => (contractMandatoryDocumentTypes.includes(x) ? x : null))
        .filter((x) => x);

      documentsCount = documentsCount.concat(mandatoryDocumentType);
    }

    const uniqueDocumentsCount = Array.from(new Set(documentsCount));

    return uniqueDocumentsCount.length < contractMandatoryDocumentTypes.length;
  };

  const handleClickDownloadContractDocumentByIndex = (documentIndex) => {
    downloadContractDocumentPdfByIndex({
      customerContractId: contractData.id,
      documentIndex
    });
  };

  const renderContractDocuments = () => {
    return (
      <table className={classes.documentTable}>
        <thead>
          <th className={classes.documentTable}>ESTADO</th>
          <th></th>
          {contractMandatoryDocumentTypes.map((documentType) => (
            <th className={classes.documentTable}>
              {t(documentType).toUpperCase()}
            </th>
          ))}
          {OPTIONAL_DOCUMENT_TYPES.map((documentType) => (
            <th className={classes.optionalDocumentTable}>
              {t(documentType).toUpperCase()}
            </th>
          ))}
        </thead>
        <tbody>
          <tr className={classes.documentTable}>
            {hasContractSomePendingDocument() ? (
              <td className={classes.documentPending}>PENDIENTE</td>
            ) : (
              <td className={classes.documentUploaded}>OK</td>
            )}

            <td></td>
            {contractMandatoryDocumentTypes.map((documentType) =>
              hasContractDocumentType(documentType) ? (
                <td className={classes.documentUploaded}>OK</td>
              ) : (
                <td className={classes.documentPending}>X</td>
              )
            )}

            {OPTIONAL_DOCUMENT_TYPES.map((documentType) =>
              hasContractDocumentType(documentType) ? (
                <td className={classes.optionalDocumentUploaded}>OK</td>
              ) : (
                <td></td>
              )
            )}
          </tr>

          <tr>
            <td colSpan="9">
              <hr></hr>
            </td>
          </tr>
          <tr>
            <td></td>
          </tr>
          <tr>
            <td></td>
          </tr>
          <tr>
            <td></td>
          </tr>
          <tr>
            <td></td>
          </tr>
          <tr>
            <td></td>
          </tr>
          <tr>
            <td></td>
          </tr>
          <tr>
            <td></td>
          </tr>
          <tr>
            <td></td>
          </tr>
          <tr>
            <td></td>
          </tr>
          <tr>
            <td></td>
          </tr>
          <tr>
            <td></td>
          </tr>
          <tr>
            <td></td>
          </tr>
          <tr>
            <td></td>
          </tr>
          <tr>
            <td></td>
          </tr>
          <tr>
            <td></td>
          </tr>
          <tr>
            <td></td>
          </tr>
          <tr>
            <td></td>
          </tr>
          <tr>
            <td></td>
          </tr>
          <tr>
            <td></td>
          </tr>
          <tr>
            <td></td>
          </tr>

          {contractData && contractData.documents ? (
            contractData.documents.map((contractDocumentType, index) => (
              <tr>
                <td className={classes.documentTable}>
                  {moment(contractDocumentType.timestamp)
                    .locale('es')
                    .format('DD/MM/YYYY hh:mm:ss')}
                  <br></br>
                  {contractDocumentType.userFullName || ''}
                </td>
                <td>
                  <SvgIcon
                    className={classes.downloadButton}
                    onClick={(event) =>
                      handleClickDownloadContractDocumentByIndex(index)
                    }
                    fontSize="small"
                    color="action"
                  >
                    <DownloadIcon />
                  </SvgIcon>
                </td>
                {contractMandatoryDocumentTypes.map((documentType) =>
                  contractDocumentType.type.includes(documentType) ? (
                    <td className={classes.documentUploaded}>OK</td>
                  ) : (
                    <td className={classes.documentTable}> </td>
                  )
                )}

                {OPTIONAL_DOCUMENT_TYPES.map((documentType) =>
                  contractDocumentType.type.includes(documentType) ? (
                    <td className={classes.documentUploaded}>OK</td>
                  ) : (
                    <td className={classes.documentTable}> </td>
                  )
                )}
              </tr>
            ))
          ) : (
            <></>
          )}
        </tbody>
      </table>
    );
  };

  const renderContractDocumentsDialog = () => {
    return (
      <Dialog
        className={classes.contractDocumentsDialog}
        onClose={handleCloseContractDocumentsDialog}
        aria-labelledby="contract-documents-dialog"
        open={showContractDocumentsDialog}
        maxWidth="false"
      >
        <div className={classes.documentsDialogContainer}>
          <DialogTitle id="contract-documents-dialog">Documentos</DialogTitle>

          {showContractDocumentUploadDialog ? (
            <List>
              <input type="file" id="file" name="file" />
              <br />
              {renderSelectContractDocumentType()}
              <ListItem
                className={classes.dialogListItemPrimary}
                button
                onClick={handleUploadDocument}
                key={'UPLOAD_DOCUMENT'}
              >
                <ListItemText primary={'Subir'} />
              </ListItem>
              <ListItem
                className={classes.dialogListItemSecondary}
                button
                onClick={handleCloseUploadDocumentDialog}
                key={'CANCEL_UPLOAD_DOCUMENT'}
              >
                <ListItemText primary={'Cancelar'} />
              </ListItem>
            </List>
          ) : (
            <>
              {renderContractDocuments()}

              <List>
                <ListItem
                  className={classes.dialogListItemPrimary}
                  button
                  onClick={handleShowUploadDocument}
                  key={'ADD_DOCUMENT'}
                >
                  <ListItemText primary={'Añadir documento'} />
                </ListItem>
                <ListItem
                  className={classes.dialogListItemSecondary}
                  button
                  onClick={handleCloseContractDocumentsDialog}
                  key={'CANCEL_ADD_DOCUMENT'}
                >
                  <ListItemText primary={'Cancelar'} />
                </ListItem>
              </List>
            </>
          )}
        </div>
      </Dialog>
    );
  };

  const handleSelectContractDocumentType = (event) => {
    const { name } = event.target;

    let values = documentUploadType;

    if (values[name]) {
      delete values[name];
    } else {
      values[name] = name;
    }

    console.log(values);
    setDocumentUploadType(values);
  };

  const isDocumentTypeChecked = (type) => documentUploadType[type];

  const renderSelectContractDocumentType = () => {
    const name = 'document-type';

    return (
      <FormControl className={classes.formControl} key={name}>
        {documentUploadType &&
          contractMandatoryDocumentTypes
            .concat(OPTIONAL_DOCUMENT_TYPES)
            .map((option) => (
              <FormControlLabel
                className={classes.checkboxFieldDocument}
                value={t(option)}
                control={
                  <Checkbox
                    name={option}
                    color="primary"
                    checked={isDocumentTypeChecked(option)}
                    onChange={handleSelectContractDocumentType}
                  />
                }
                label={t(option).toUpperCase()}
                labelPlacement="end"
              />
            ))}
      </FormControl>
    );
  };

  const handleShowContractDocuments = () => {
    setShowContractDocumentsDialog(true);
  };

  const renderButtonList = () => {
    return (
      <Box display="flex" justifyContent="flex-end">
        <Button className={classes.backButton} onClick={cancel}>
          {'Volver'}
        </Button>

        {contractData && contractData.id && (
          <Button
            color={'primary'}
            className={
              hasContractSomePendingDocument()
                ? classes.documentButtonRed
                : classes.documentButton
            }
            onClick={handleShowContractDocuments}
          >
            {'Documentos'}
          </Button>
        )}

        {contractData &&
        contractData.contractCodePrefix &&
        ['R', 'C'].includes(contractData.contractCodePrefix) &&
        contractData.ContractStatus &&
        [
          'ACTIVE',
          'SERVICE_CUT',
          'SERVICE_AND_BILLING_CUT',
          'TEMPORARY_SUSPENSION'
        ].includes(contractData.ContractStatus.code) ? (
          <Button
            color="primary"
            className={classes.renewalButton}
            onClick={showRenewalDialog}
          >
            Renovar
          </Button>
        ) : (
          ''
        )}

        {contractData &&
        contractData.ContractStatus &&
        contractData.ContractStatus.code === 'ACTIVE_HOLDER_CHANGE' ? (
          <Button className={classes.searchButton} onClick={searchCustomer}>
            Buscar cliente
          </Button>
        ) : (
          ''
        )}

        {contractData &&
        contractData.contractCodePrefix === 'R' &&
        contractData.ContractStatus &&
        [
          'PENDING_INSTALLATION',
          'ACTIVE',
          'ACTIVE_PENDING_PAYMENT',
          'INACTIVE_PENDING_UNINSTALLATION',
          'SERVICE_CUT',
          'TEMPORARY_SUSPENSION'
        ].includes(contractData.ContractStatus.code) ? (
          <Button
            color="primary"
            className={classes.testInstallationButton}
            onClick={showTestInstallation}
          >
            Pruebas instalación
          </Button>
        ) : (
          ''
        )}

        {isInactiveReinstallation() ? (
          contractData && !contractData.reinstallationDestinContractId ? (
            <Button
              color="primary"
              variant="contained"
              className={classes.saveButton}
              onClick={reinstallContract}
            >
              Reinstalar
            </Button>
          ) : (
            <Button className={classes.backButton}>REINSTALADO</Button>
          )
        ) : isInactiveHolderChange() ? (
          contractData && !contractData.holderChangeDestinContractId ? (
            <Button
              color="primary"
              variant="contained"
              className={classes.saveButton}
              onClick={changeHolderContract}
            >
              Cambiar titular
            </Button>
          ) : (
            <Button className={classes.backButton}>TITULAR MIGRADO</Button>
          )
        ) : (
          <Button
            color="primary"
            variant="contained"
            className={classes.saveButton}
            onClick={postForm}
          >
            Guardar contrato
          </Button>
        )}
      </Box>
    );
  };

  const getRenewalDate = () => {
    if (!contractData) {
      return '';
    }

    const currentContractType = contractTypeList.find(
      (x) => x.id === contractData.contractTypeId
    );
    const duration =
      currentContractType && currentContractType.totalDuration
        ? currentContractType.totalDuration
        : 24;

    const startDate = contractData.renewalDate || contractData.beginDate;
    const endDateAfterDuration = moment(startDate)
      .add(duration, 'months')
      .set('date', 1)
      .set('hour', 0)
      .set('minute', 0)
      .set('second', 0)
      .set('millisecond', 0);

    const offset = endDateAfterDuration < new Date() ? 0 : 0;
    return moment(endDateAfterDuration)
      .add(offset, 'month')
      .format('DD/MM/YYYY');
  };

  const renderRenewalDialog = () => {
    return (
      <Dialog
        onClose={handleCloseContractRenewalDialog}
        aria-labelledby="contract-renewal-dialog"
        open={showContractRenewalDialog}
        maxWidth="false"
      >
        <div className={classes.dialogContainer}>
          <DialogTitle id="contract-renewal-dialog">
            Renovar contrato
          </DialogTitle>
          <h3>
            ¿Desea renovar el contrato? Se renovará con fecha:{' '}
            {getRenewalDate()}
          </h3>
          <List>
            <ListItem
              className={classes.dialogListItemPrimary}
              button
              onClick={renewContract}
              key={'RENEW_CONTRACT'}
            >
              <ListItemText primary={'Renovar'} />
            </ListItem>
            <ListItem
              className={classes.dialogListItemSecondary}
              button
              onClick={handleCloseContractRenewalDialog}
              key={'CANCEL_RENEW_DOCUMENT'}
            >
              <ListItemText primary={'Cancelar'} />
            </ListItem>
          </List>
        </div>
      </Dialog>
    );
  };

  const renewContract = async () => {
    handleCloseContractRenewalDialog();

    if (isRequesting) {
      return;
    }

    setIsRequesting(true);

    const result = await renewContractById(contractData.id);

    setIsRequesting(false);

    if (result && result.error) {
      alert(t(result.error) || 'Se ha producido un error, vuelva a intentarlo');
      return;
    }

    alert('El contrato ha sido renovado correctamente');
  };

  const showRenewalDialog = () => {
    setShowContractRenewalDialog(true);
  };

  const handleCloseContractRenewalDialog = () => {
    setShowContractRenewalDialog(false);
  };

  return (
    <Page className={classes.root} title="Cliente">
      {renderContractDocumentsDialog()}
      {renderInstallationDialog()}
      {renderRenewalDialog()}

      <Container maxWidth={false}>
        <Box className={classes.navigation} display="flex">
          <Typography color="textSecondary" variant="body2">
            <Link component={RouterLink} to="" onClick={cancel}>
              Contratos
            </Link>
            {` >  ${getContractCode()}`}
          </Typography>
        </Box>
        {renderButtonList()}
        <Card>
          <Form
            fields={formFields}
            onChangeFields={handleChangeFormFieldsValues}
          />
        </Card>
      </Container>
    </Page>
  );
};

export default ContractForm;
